import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/shared/lib/utils';
import { Badge } from '@/shared/ui';
import { usePermission } from '@/shared/auth';
import { PermissionAccessLevel } from '@/api/generated/schemas/permissionAccessLevel';
import type { PermissionCatalogNode } from '@/api/generated/schemas/permissionCatalogNode';
import {
  getGetRolePermissionGrantsQueryKey,
  useGetPermissionCatalog,
  useGetRolePermissionGrants,
  useGetSecurityRoles,
  useSetRolePermissionGrant,
} from '@/api/generated/security/security';
import { SECURITY_ROLE_PERMISSIONS } from '../permissions';
import { computeEffectiveLevels } from './effectiveLevels';
import { PermissionLevelControl } from './components/PermissionLevelControl';

interface RolePermissionsPageProps {
  layerId: string;
}

const LEVEL_LABEL_KEY: Record<PermissionAccessLevel, string> = {
  None: 'level.none',
  Read: 'level.read',
  Edit: 'level.edit',
};

/**
 * Admin agenda správy oprávnění rolí: vlevo výběr role, vpravo strom permission katalogu
 * s úrovní per uzel (ukládá se hned při změně), náhledem zděděné úrovně a sekcí osiřelých grantů.
 */
export function RolePermissionsPage(_props: RolePermissionsPageProps) {
  const { t } = useTranslation('security');
  const queryClient = useQueryClient();
  const canEdit = usePermission(SECURITY_ROLE_PERMISSIONS) === 'edit';

  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [saveError, setSaveError] = useState(false);

  const rolesQuery = useGetSecurityRoles();
  const catalogQuery = useGetPermissionCatalog();
  // roleId 0 = žádná vybraná role; generovaný hook má enabled: !!roleId, takže se dotaz nespustí.
  const grantsQuery = useGetRolePermissionGrants(selectedRoleId ?? 0);

  const setGrant = useSetRolePermissionGrant({
    mutation: {
      onSuccess: async (_data, variables) => {
        setSaveError(false);
        await queryClient.invalidateQueries({
          queryKey: getGetRolePermissionGrantsQueryKey(variables.roleId),
        });
      },
      onError: () => setSaveError(true),
    },
  });

  const nodes = useMemo(() => catalogQuery.data?.nodes ?? [], [catalogQuery.data]);
  const grants = useMemo(() => grantsQuery.data?.grants ?? [], [grantsQuery.data]);

  const childrenByParent = useMemo(() => {
    const map = new Map<string | null, PermissionCatalogNode[]>();
    for (const node of nodes) {
      const key = node.parentKey ?? null;
      const list = map.get(key);
      if (list) list.push(node);
      else map.set(key, [node]);
    }
    return map;
  }, [nodes]);

  const levels = useMemo(() => computeEffectiveLevels(nodes, grants), [nodes, grants]);
  const orphans = useMemo(() => grants.filter((g) => g.isOrphan), [grants]);

  const nodeLabel = (key: string) => t(`nodes.${key}.label`, { defaultValue: key });

  const changeLevel = (permissionKey: string, level: PermissionAccessLevel) => {
    if (selectedRoleId === null) return;
    setGrant.mutate({ roleId: selectedRoleId, data: { permissionKey, level } });
  };

  const renderNode = (node: PermissionCatalogNode, depth: number) => {
    const info = levels.get(node.key);
    const children = childrenByParent.get(node.key) ?? [];

    return (
      <div key={node.key}>
        <div
          className="border-border/50 flex items-center gap-2 border-b py-1.5 pr-2"
          style={{ paddingLeft: `${depth * 1.25}rem` }}
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="truncate text-sm">{nodeLabel(node.key)}</span>
              {!node.inheritsFromParent && <Badge tone="warning">{t('tree.notInherited')}</Badge>}
              {node.sensitiveRead && <Badge tone="info">{t('tree.sensitiveRead')}</Badge>}
            </div>
            <div className="text-muted-foreground text-xs">
              <code>{node.key}</code>
              {info?.inheritedFrom && (
                <span className="ml-2">
                  {t('tree.inheritedFrom', {
                    level: t(LEVEL_LABEL_KEY[info.effective]),
                    key: nodeLabel(info.inheritedFrom),
                  })}
                </span>
              )}
            </div>
          </div>
          <PermissionLevelControl
            value={info?.granted ?? PermissionAccessLevel.None}
            disabled={!canEdit || setGrant.isPending}
            onChange={(level) => changeLevel(node.key, level)}
          />
        </div>
        {children.map((child) => renderNode(child, depth + 1))}
      </div>
    );
  };

  const roots = childrenByParent.get(null) ?? [];

  return (
    <div className="flex h-full min-h-0">
      {/* Seznam rolí */}
      <div className="border-border flex w-64 shrink-0 flex-col border-r">
        <div className="border-border border-b px-3 py-2 text-sm font-semibold">
          {t('roles.title')}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {rolesQuery.isError && (
            <p className="text-destructive p-3 text-sm">{t('roles.loadError')}</p>
          )}
          {rolesQuery.data && rolesQuery.data.roles.length === 0 && (
            <p className="text-muted-foreground p-3 text-sm">{t('roles.empty')}</p>
          )}
          {rolesQuery.data?.roles.map((role) => (
            <button
              key={role.id}
              type="button"
              className={cn(
                'hover:bg-muted block w-full px-3 py-2 text-left text-sm',
                selectedRoleId === role.id && 'bg-muted font-medium'
              )}
              onClick={() => {
                setSelectedRoleId(role.id);
                setSaveError(false);
              }}
            >
              <div className="truncate">{role.name}</div>
              {role.description && (
                <div className="text-muted-foreground truncate text-xs">{role.description}</div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Strom oprávnění */}
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {selectedRoleId === null ? (
          <p className="text-muted-foreground text-sm">{t('tree.selectRole')}</p>
        ) : catalogQuery.isError || grantsQuery.isError ? (
          <p className="text-destructive text-sm">{t('tree.loadError')}</p>
        ) : (
          <>
            {!canEdit && <p className="text-muted-foreground mb-3 text-sm">{t('tree.readOnly')}</p>}
            {saveError && <p className="text-destructive mb-3 text-sm">{t('saveError')}</p>}

            <div>{roots.map((node) => renderNode(node, 0))}</div>

            {orphans.length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-semibold">{t('orphans.title')}</h3>
                <p className="text-muted-foreground mb-2 text-xs">{t('orphans.hint')}</p>
                {orphans.map((grant) => (
                  <div
                    key={grant.permissionKey}
                    className="border-border/50 flex items-center gap-2 border-b py-1.5"
                  >
                    <code className="flex-1 truncate text-xs">{grant.permissionKey}</code>
                    <span className="text-muted-foreground text-xs">
                      {t(LEVEL_LABEL_KEY[grant.level])}
                    </span>
                    <button
                      type="button"
                      disabled={!canEdit || setGrant.isPending}
                      className="text-destructive text-xs hover:underline disabled:cursor-not-allowed disabled:opacity-60"
                      onClick={() => changeLevel(grant.permissionKey, PermissionAccessLevel.None)}
                    >
                      {t('orphans.remove')}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
